import { CSSProperties } from 'react';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import styles from './HeroCards.module.css';

/* Photon Hero — four pre-rendered card PNGs anchored to the device
   image. Each PNG is a complete card (thumb + text + state),
   so this component just lays them out at the Figma-defined positions
   and fades them in once on mount.

   Positions taken from Figma node 899:4272 and expressed as percentages
   of the device's intrinsic 532 × 516.45 footprint. Each card is placed
   inside ProductInner (sized to the device image), so the anchors track
   the device as the image scales horizontally.

   Below 950px viewport height the device shrinks via the height-driven
   max-width upstream; each card adopts the same scale factor so cards
   and device shrink in lockstep. */

const FACE_CARD = 'web_v2/photon/landing-page/hero-cards/face-v3.png';
const ARM_CARD = 'web_v2/photon/landing-page/hero-cards/arms-v3.png';
const JOINT_CARD = 'web_v2/photon/landing-page/hero-cards/joint-care-v3.png';
const BACK_CARD = 'web_v2/photon/landing-page/hero-cards/back-v2.png';

const DEVICE_W = 532;
const DEVICE_H = 516.45;
const DEVICE_LEFT = 490;
const DEVICE_TOP = 428.35;

type CardSpec = {
  src: string;
  alt: string;
  w: number;
  centerX: number;
  centerY: number;
  naturalW: number;
  naturalH: number;
  delay: number;
  /* Final opacity once the card has faded in. Cards 1 (Face) and 3
     (Joint Care) sit on top → opacity 1. Cards 2 (Arm) and 4 (Back)
     are dimmed back → opacity 0.5. */
  opacity: number;
  /* Elevated cards get a soft white drop-shadow underneath so they
     feel lifted above the dimmed ones. */
  elevated: boolean;
};

const CARDS: CardSpec[] = [
  {
    src: FACE_CARD,
    alt: 'Face inflammation notification',
    w: 340,
    centerX: 334,
    centerY: 566,
    naturalW: 2720,
    naturalH: 688,
    delay: 0.35,
    opacity: 1,
    elevated: true,
  },
  {
    src: ARM_CARD,
    alt: 'Arm recovery session notification',
    w: 425,
    centerX: 439.5,
    centerY: 768.5,
    naturalW: 3400,
    naturalH: 760,
    delay: 0.6,
    opacity: 0.4,
    elevated: false,
  },
  {
    src: JOINT_CARD,
    alt: 'Joint Care session in progress',
    w: 364,
    centerX: 1107,
    centerY: 524,
    naturalW: 2912,
    naturalH: 944,
    delay: 0.45,
    opacity: 1,
    elevated: true,
  },
  {
    src: BACK_CARD,
    alt: 'Back recovery notification',
    w: 369,
    centerX: 1082.5,
    centerY: 759.9,
    naturalW: 2952,
    naturalH: 687,
    delay: 0.65,
    opacity: 0.5,
    elevated: false,
  },
];

/* Order the cards into the on-screen sequence we want them to appear,
   not the order they're listed in CARDS. Sequence: Face → Joint Care
   (the elevated pair) → Arm → Back (the dimmed pair). */
const REVEAL_ORDER: Array<CardSpec['src']> = [
  FACE_CARD,
  JOINT_CARD,
  ARM_CARD,
  BACK_CARD,
];

/* Reveal kicks in after the hero image has had a moment to paint, then
   each card fades in one after the next. Tweak BASE_DELAY upstream to
   align with the hero's own entrance pacing. */
const BASE_DELAY = 0.9; // seconds — wait for hero image to settle
const STAGGER = 0.35; // seconds between successive cards

const HeroCards = () => (
  <div className={styles.cardLayer} aria-hidden="true">
    {CARDS.map((c) => {
      const leftPct = ((c.centerX - DEVICE_LEFT) / DEVICE_W) * 100;
      const topPct = ((c.centerY - DEVICE_TOP) / DEVICE_H) * 100;
      const renderedH = (c.w * c.naturalH) / c.naturalW;
      const idx = REVEAL_ORDER.indexOf(c.src);
      const delay = BASE_DELAY + idx * STAGGER;
      return (
        <div
          key={c.src}
          className={styles.card}
          data-elevated={c.elevated}
          style={
            {
              ['--card-left']: `${leftPct}%`,
              ['--card-top']: `${topPct}%`,
              ['--card-w']: `${c.w}px`,
              ['--card-h']: `${renderedH}px`,
              ['--card-delay']: `${delay}s`,
              ['--final-opacity']: c.opacity,
            } as CSSProperties
          }
        >
          <CustomImage
            src={c.src}
            alt={c.alt}
            width={c.naturalW}
            height={c.naturalH}
            sizes={`${Math.ceil(c.w)}px`}
            priority
          />
        </div>
      );
    })}
  </div>
);

export default HeroCards;
